import React from "react";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "../../Templates/Header";
import { devicesRegistry } from "./data/deviceRegistrationSteps";
import { usePanelTokens } from "../../../utils/panelTokens";

/* ── Guide card — active guide ── */
const GuideCard = ({ doc, deviceName, onClick }) => {
  const T = usePanelTokens();
  const [hovered, setHovered] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.ground,
        border: `1px solid ${hovered ? T.accent : T.border}`,
        borderRadius: "10px",
        overflow: "hidden",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s, transform 0.2s",
        transform: hovered ? "translateY(-3px)" : "none",
        boxShadow: hovered
          ? "0 0 0 1px rgba(58,142,255,0.35), 0 8px 28px rgba(58,142,255,0.12)"
          : "none",
      }}
    >
      {/* Thumbnail */}
      <div
        style={{
          height: "180px",
          background: T.surface,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {!imgError && doc.thumbnail && (
          <img
            src={doc.thumbnail}
            alt={doc.title}
            onError={() => setImgError(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        )}

        {/* Play overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: hovered ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.3)",
            transition: "background 0.25s",
          }}
        >
          <PlayCircleOutlineIcon
            style={{
              fontSize: "48px",
              color: "rgba(255,255,255,0.9)",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))",
              transform: hovered ? "scale(1.1)" : "scale(1)",
              transition: "transform 0.2s",
            }}
          />
        </div>

        {/* Category chip */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            background: T.accent,
            color: "#fff",
            fontSize: "10px",
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: "4px",
            letterSpacing: "0.05em",
          }}
        >
          {deviceName}
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: "16px 18px 18px" }}>
        <p
          style={{
            margin: "0 0 6px",
            fontSize: "14px",
            fontWeight: 600,
            color: T.text,
            lineHeight: 1.4,
          }}
        >
          {doc.title}
        </p>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: "12px",
            color: T.textMuted,
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {doc.description}
        </p>

        <div style={{ height: "1px", background: T.border, margin: "0 -2px 12px" }} />

        {/* Meta row */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            fontSize: "11px",
            color: T.textMuted,
          }}
        >
          <span>{doc.stepCount} steps</span>
          <span>{doc.duration}</span>
          <span style={{ marginLeft: "auto" }}>{doc.lastUpdated}</span>
        </div>
      </div>
    </div>
  );
};

/* ── Coming soon placeholder card ── */
const ComingSoonCard = () => {
  const T = usePanelTokens();
  return (
  <div
    style={{
      background: T.ground,
      border: `1px solid ${T.border}`,
      borderRadius: "10px",
      overflow: "hidden",
      opacity: 0.6,
    }}
  >
    {/* Placeholder thumbnail */}
    <div
      style={{
        height: "180px",
        background: T.surface,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PlayCircleOutlineIcon
        style={{ fontSize: "48px", color: T.placeholderIcon }}
      />
      {/* Coming soon chip */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "10px",
          background: T.chipBg,
          border: `1px solid ${T.chipBorder}`,
          color: T.chipText,
          fontSize: "10px",
          fontWeight: 700,
          padding: "3px 8px",
          borderRadius: "4px",
          letterSpacing: "0.05em",
        }}
      >
        COMING SOON
      </div>
    </div>

    {/* Skeleton body */}
    <div style={{ padding: "16px 18px 18px" }}>
      <div
        style={{
          height: "14px",
          width: "68%",
          background: T.skeleton[0],
          borderRadius: "4px",
          marginBottom: "8px",
        }}
      />
      <div
        style={{
          height: "11px",
          width: "90%",
          background: T.skeleton[1],
          borderRadius: "4px",
          marginBottom: "5px",
        }}
      />
      <div
        style={{
          height: "11px",
          width: "55%",
          background: T.skeleton[2],
          borderRadius: "4px",
          marginBottom: "16px",
        }}
      />
      <p
        style={{
          margin: 0,
          fontSize: "11px",
          color: T.textFaint,
          fontStyle: "italic",
        }}
      >
        More guides coming soon
      </p>
    </div>
  </div>
  );
};

/* ── Main component ── */
const DeviceGuidesList = ({ deviceId, onSelectDoc, onBack }) => {
  const T = usePanelTokens();
  const device = devicesRegistry.find((d) => d.id === deviceId);
  if (!device) return null;

  const placeholderCount = Math.max(0, 3 - device.guides.length);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems:"flex-start",
        minHeight: "100%",
        background: T.ground,
        color: T.text,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* ── HEADER (same as all other pages) ── */}
      <Header
        title="Documentation"
        subtitle="Step-by-step guides, API references, and tutorials for the Vecros platform."
      />

      {/* ── CONTENT ── */}
      <div style={{ flex: 1, padding: "32px 10px" }}>
        {/* Breadcrumb row */}
        <div
          style={{
            display: "flex",
            flexDirection:"column",
            alignItems: 'flex-start',
            gap: "10px",
            marginBottom: "28px",
            
          }}
        >
          <button
            onClick={onBack}
            style={{
              background: "transparent",
              border: `1px solid ${T.border}`,
              borderRadius: "6px",
              color: T.textMuted,
              cursor: "pointer",
              padding: "5px 8px",
              display: "flex",
              alignItems: "center",
              marginRight: "4px",
            }}
          >
            <ArrowBackIcon style={{ fontSize: "15px" }} />
          </button>

          <span
            onClick={onBack}
            style={{
              fontSize: "14px",
              color: T.accent,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Devices
          </span>
          <span style={{ fontSize: "14px", color: T.textFaint }}>{">"}</span>
          <span
            style={{
              display:"block",
              width:"100",
              textAlign:"left",
              fontSize: "14px",
              fontWeight: 700,
              color: T.text,
              letterSpacing: "0.04em",
              
            }}
          >
            {device.name}
          </span>
          <div style={{ flex: 1, height: "1px", background: T.border }} />
        </div>

        {/* Guide cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px",
          }}
        >
          {device.guides.map((doc) => (
            <GuideCard
              key={doc.id}
              doc={doc}
              deviceName={device.name}
              onClick={() => onSelectDoc(doc.id)}
            />
          ))}
          {[...Array(placeholderCount)].map((_, i) => (
            <ComingSoonCard key={`soon-${i}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DeviceGuidesList;
